// src/types/vitals-api.ts
// Vitals wire types — match the .NET wire shape verbatim. Ported from the PM app
// (dev-scrips-pm-react/src/types/vitals-api.ts), itself curl-verified 2026-05-15
// against GET /api/Patients/GetVitalsDetailsByEncounter + GetEncounterVitalsHistory.
// Flutter parity: patient_vitals_model.dart + encounter_vitals_history_data.dart.
//
// IMPORTANT: the wire field is `identifire` (misspelled) — the .NET binder is
// case/spelling-sensitive. Match verbatim.
//
// Fields are OPTIONAL here (the pre-existing PA READ surfaces construct partial
// PatientVital literals). The vitals-history port (PROD-1535) reads the fuller set
// (createdDate/lmp/hcm/extraData/…) and the flat→nested history projection below.

export interface VitalCode {
  /** LOINC code (or Scrips-local sentinel like `39156-5` for BMI / `11-5` for Bioage). */
  code: string | null;
  /** Capital D — backend ships it with this casing. */
  Definition?: string | null;
  display?: string | null;
  system?: string | null;
  description?: string | null;
  shortName?: string | null;
}

export interface VitalQuantity {
  /** Free-text value — numeric vitals ship as stringified numbers; LMP ships a date
   *  like "May 04, 2026"; visual acuity ships "20/20/20/20". */
  value: string | null;
  unit: string | null;
  system?: string | null;
  /** Mirrors `code.code` — backend redundantly stamps the LOINC here. */
  code?: string | null;
}

export interface PatientVital {
  /** Note misspelled "identifire" — verbatim wire field name. */
  identifire?: string | null;
  status?: string | null;
  code: VitalCode | null;
  quantity: VitalQuantity | null;
  subject?: string | null;
  encounter?: string | null;
  category?: string | null;
  dataAbsentReason?: string | null;
  deleteProvider?: string | null;
  /** No-Z ISO datetime (`YYYY-MM-DDTHH:mm:ss.sss`). */
  effectiveDateTime?: string | null;
  /** `"normal"` | `"abnormal"` | `null` — backend-computed, not a client threshold. */
  vitalNormalStatus?: string | null;
  /** Stringified decimal — backend computes vs the catalog normalValue. */
  vitalAbnormalPercentage?: string | null;
  lmp?: string | null;
  hcm?: string | null;
  /** Visual-acuity vital (LOINC 31479-8) extra payload (PROD-1537) — verbatim Flutter wire field
   *  `extraData` (VisualAcuityData JSON: distance/near/correction_type). Absent for other vitals. */
  extraData?: import('@/encounter/lib/visual-acuity').VisualAcuityJson | null;
  isDeleted?: boolean | null;
  /** No-Z ISO datetime. */
  createdDate?: string | null;
  isMenstrual?: boolean | null;
  isBreastFeeding?: boolean | null;
}

/** GET /api/Patients/GetVitalsDetailsByEncounter → bare array (no envelope). */
export type GetEncounterVitalsResponse = PatientVital[];

/**
 * LOINC codes for vital signs — copied verbatim from Flutter
 * `scrips_core/lib/constants/app_constants.dart`. These ARE the wire values: the
 * backend's vitals controller keys on `code.code`, so any drift silently breaks
 * server-side normal-range routing.
 *
 * The 2 sentinel codes (`BIOAGE = "11-5"`, `BMI = "39156-5"`) are Scrips-local,
 * not real LOINC.
 *
 * Head circumference has ONE code, `HCM` (`11947-9`), as Flutter saves and reads it
 * (`app_constants.dart:269`, `growth_chart.dart:273`). Andrew, 9 Oct (PROD-3185): vitals and the
 * growth chart follow Flutter exactly. It is saved with no unit (`ui_data.dart`), so a missing
 * unit reads as cm.
 */
export const VITAL_CODES = {
  /** Body temperature. */
  TEMPERATURE: '8310-5',
  /** Heart rate / pulse. */
  HEART_RATE: '8867-4',
  /** Systolic BP. Combo-renders with diastolic into a single cell. */
  SYSTOLIC: '8480-6',
  /** Diastolic BP. */
  DIASTOLIC: '8462-4',
  /** Respiratory rate. */
  RESP_RATE: '9279-1',
  /** Oxygen saturation. */
  O2_SAT: '59408-5',
  /** Height. */
  HEIGHT: '8302-2',
  /** Weight. */
  WEIGHT: '29463-7',
  /** BMI — computed by backend from height/weight; UI cell is read-only. */
  BMI: '39156-5',
  /** Last menstrual period start date. Gated on gender=Female, age>8. */
  LMP: '8665-2',
  /** Head circumference: the vitals form, the vitals row, the chart summary and the growth chart.
   *  Gated on age <= 2. Saved with no unit; read as cm. */
  HCM: '11947-9',
  /** Hearing test result (free-text `"<Left>/<Right>"`). */
  HEARING: '62977-4',
  /** Visual acuity. Stores `20/20/20/20` plus an `extraData` blob. */
  VISUAL: '31479-8',
  /** Bioage. Scrips-local code (not real LOINC). */
  BIOAGE: '11-5',
  /** Hip circumference. */
  HIP: '8281-8',
  /** Waist circumference. */
  WAIST: '8280-0',
  /** Waist-to-hip ratio — computed by backend from hip/waist. */
  WAIST_TO_HIP_RATIO: '56115-9',
} as const;

export type VitalCodeKey = keyof typeof VITAL_CODES;
export type VitalCodeValue = (typeof VITAL_CODES)[VitalCodeKey];

/**
 * Set of codes the backend computes — UI should NOT accept user input for these.
 * Flutter `vital_utils.dart` marks them `isEditable: false`.
 */
export const COMPUTED_VITAL_CODES: ReadonlySet<string> = new Set([
  VITAL_CODES.BMI,
  VITAL_CODES.WAIST_TO_HIP_RATIO,
]);

/**
 * One row in `EncounterVitalData.observationsVitals` — the FLAT wire shape returned
 * by `GET /api/Patients/GetEncounterVitalsHistory`. Mirrors Flutter `ObservationsVital`
 * in `encounter_vitals_history_data.dart:112-206`. Distinct from `PatientVital`: that
 * one is the nested CREATE/UPDATE shape (`code.code`, `quantity.value`); this one
 * flattens everything to top-level fields (`loincCode`, `quantityValue`, …). Two
 * parallel server-side projections, NOT interchangeable.
 */
export interface ObservationsVital {
  id: string | null;
  loincCode: string | null;
  loincDisplay: string | null;
  loincSystem: string | null;
  status: string | null;
  category: string | null;
  encounter: string | null;
  subject: string | null;
  effectiveDateTime: string | null;
  quantityValue: string | null;
  quantityUnit: string | null;
  quantitySystem: string | null;
  quantityCode: string | null;
  dataAbsentReason: string | null;
  vitalNormalStatus: string | null;
  vitalAbnormalPercentage: string | null;
  lmp: string | null;
  hcm: string | null;
  isMenstrual: boolean | null;
  isBreastFeeding: boolean | null;
  isActive: boolean | null;
  isDeleted: boolean | null;
  createdBy: string | null;
  createdDate: string | null;
  updatedby: string | null;
  updatedDate: string | null;
  observationsVitalsCaptureId: string | null;
}

/**
 * One encounter's worth of vitals as returned by
 * `GET /api/Patients/GetEncounterVitalsHistory`. Mirrors Flutter `EncounterVitalData`
 * in `encounter_vitals_history_data.dart:46-94`. `observationsVitals` is the FLAT
 * `ObservationsVital` projection, NOT the nested `PatientVital` shape — convert with
 * `observationsVitalToPatientVital()` at render time.
 */
export interface EncounterVitalData {
  id: string | null;
  encounter: string | null;
  subject: string | null;
  isActive: boolean | null;
  createdBy: string | null;
  createdByName: string | null;
  /** No-Z ISO datetime per the wire-shape convention. */
  createdDate: string | null;
  updatedBy: string | null;
  updatedDate: string | null;
  observationsVitals: ObservationsVital[] | null;
}

/**
 * Flat → nested converter. Mirrors Flutter `ObservationsVital.toPatientVitals()` in
 * `encounter_vitals_history_data.dart:238-269`. Used by the history drawer to reuse
 * the cell render logic which expects the `PatientVital` (nested) shape.
 *
 * Deliberately UNMAPPED (Flutter parity — `toPatientVitals` drops these too):
 *   isActive / createdBy / updatedby / updatedDate / observationsVitalsCaptureId.
 */
export function observationsVitalToPatientVital(
  obs: ObservationsVital,
): PatientVital {
  return {
    identifire: obs.id,
    status: obs.status,
    code: {
      code: obs.loincCode,
      Definition: null,
      display: obs.loincDisplay,
      system: obs.loincSystem,
      description: null,
      shortName: null,
    },
    quantity: {
      value: obs.quantityValue,
      unit: obs.quantityUnit,
      system: obs.quantitySystem,
      code: obs.quantityCode,
    },
    subject: obs.subject,
    encounter: obs.encounter,
    category: obs.category,
    dataAbsentReason: obs.dataAbsentReason,
    deleteProvider: null,
    effectiveDateTime: obs.effectiveDateTime,
    vitalNormalStatus: obs.vitalNormalStatus,
    vitalAbnormalPercentage: obs.vitalAbnormalPercentage,
    lmp: obs.lmp,
    hcm: obs.hcm,
    isDeleted: obs.isDeleted,
    createdDate: obs.createdDate,
    isMenstrual: obs.isMenstrual,
    isBreastFeeding: obs.isBreastFeeding,
    extraData: null,
  };
}

/**
 * Response from `GET /api/Patients/GetEncounterVitalsHistory?patientId=&encounterId=`.
 * Splits the current encounter (echoed for convenience) from prior encounters. Mirrors
 * Flutter `EncounterVitalsHistoryData` in `encounter_vitals_history_data.dart:14-44`.
 */
export interface EncounterVitalsHistoryResponse {
  currentEncounterVitals: EncounterVitalData[] | null;
  previousEncounters: EncounterVitalData[] | null;
}
